import { useState } from "react";
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { sugerenciaService } from "../../services/sugerenciaService";
import { mensajeDeError } from "../../utils/errores";

/**
 * "Sugerencias" (`/app/sugerencias`): cualquier usuario propone una mejora y el
 * backend la envía al equipo de desarrollo (una tarea en Faro).
 *
 * Se guarda la última página visitada como origen: sirve para ubicar el
 * comentario sin tener que preguntar "¿en qué pantalla te pasó?".
 */
export default function Sugerencias() {
  const location = useLocation();
  const [titulo, setTitulo] = useState("");
  const [detalle, setDetalle] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<{ ok: boolean; texto: string } | null>(null);
  const [error, setError] = useState("");

  const enviar = async () => {
    setEnviando(true);
    setError("");
    try {
      const r = await sugerenciaService.crear({
        titulo: titulo.trim(),
        detalle: detalle.trim(),
        // La página desde la que se abrió el formulario, no esta misma.
        url_origen: (location.state as { desde?: string } | null)?.desde || document.referrer || "",
      });
      setResultado({ ok: r.enviada, texto: r.detail });
      setTitulo("");
      setDetalle("");
    } catch (e) {
      setError(mensajeDeError(e, "No se pudo enviar la sugerencia."));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        Sugerencias
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        ¿Algo que falta, que estorba o que se puede hacer mejor? Cuéntanos y llega directo al equipo que
        desarrolla el sistema.
      </Typography>

      {resultado && (
        <Alert severity={resultado.ok ? "success" : "info"} sx={{ mb: 3 }} onClose={() => setResultado(null)}>
          {resultado.texto}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3.5 }, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
        <Stack spacing={2.5}>
          <TextField
            label="¿De qué se trata?"
            placeholder="En una línea: por ejemplo, «filtrar los temas por profesor»"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            inputProps={{ maxLength: 300 }}
            fullWidth
            autoFocus
          />
          <TextField
            label="Cuéntanos más (opcional)"
            placeholder="Qué esperabas que pasara, qué pasó, o cómo lo imaginas."
            value={detalle}
            onChange={(e) => setDetalle(e.target.value)}
            multiline
            minRows={5}
            fullWidth
          />

          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={enviar}
              disabled={enviando || !titulo.trim()}
            >
              Enviar sugerencia
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
